import React from 'react';
import Seo from '../components/Seo';
import { getAllPostsIncludingDisabled, getCategoryList } from '../content/config';

const ADMIN_UNLOCK_KEY = 'lmd-admin-unlocked';
const REPO_EDIT_BASE = 'https://github.com/Lemeilleurdudigital/lmd.lemeilleurdu.digital/edit/main/';

const AdminIndex = () => {
  const configuredPass = String(import.meta.env.VITE_ADMIN_PASS || '');
  const [password, setPassword] = React.useState('');
  const [isUnlocked, setIsUnlocked] = React.useState(() => sessionStorage.getItem(ADMIN_UNLOCK_KEY) === '1');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [categoryFilter, setCategoryFilter] = React.useState('all');
  const [search, setSearch] = React.useState('');
  const [copied, setCopied] = React.useState('');

  const categories = getCategoryList();
  const posts = React.useMemo(() => getAllPostsIncludingDisabled(), []);
  const postStats = React.useMemo(() => {
    const stats = { total: posts.length, published: 0, disabled: 0 };
    posts.forEach((post) => {
      if (post.status === 'disabled') {
        stats.disabled += 1;
      } else {
        stats.published += 1;
      }
    });
    return stats;
  }, [posts]);

  const filteredPosts = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return posts.filter((post) => {
      if (statusFilter !== 'all' && post.status !== statusFilter) return false;
      if (categoryFilter !== 'all' && post.category !== categoryFilter) return false;
      if (q && !String(post.title || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [posts, statusFilter, categoryFilter, search]);

  const copyText = async (value, key) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(''), 1400);
    } catch {
      setCopied('');
    }
  };

  const buildReplaceCoverInstruction = (exactPath, newCoverUrl) =>
    [
      `Ouvre ${exactPath}`,
      `Remplace la ligne "cover: ..." par cover: "${newCoverUrl}"`,
      `Remplace/ajoute la ligne "imageSource: ..." par imageSource: '${newCoverUrl}'`
    ].join('\n');

  const buildRemoveCoverInstruction = (exactPath) =>
    [
      `Ouvre ${exactPath}`,
      'Remplace la ligne "cover: ..." par cover: ""',
      'Supprime la ligne "imageSource: ..." si elle existe'
    ].join('\n');

  const handleReplaceCover = (post, exactPath) => {
    const raw = window.prompt('Nouvelle URL de cover (https://...)', String(post.cover || ''));
    const value = String(raw || '').trim();
    if (!value) return;
    if (!/^https?:\/\//i.test(value)) {
      window.alert('URL invalide. Utilise une URL complète en http(s).');
      return;
    }
    copyText(buildReplaceCoverInstruction(exactPath, value), `rc-${post.slug}`);
  };

  const handleUnlock = (e) => {
    e.preventDefault();
    if (!configuredPass || password === configuredPass) {
      sessionStorage.setItem(ADMIN_UNLOCK_KEY, '1');
      setIsUnlocked(true);
      setPassword('');
    }
  };

  if (!isUnlocked) {
    return (
      <section className="mx-auto max-w-xl px-4 py-16">
        <Seo title="Admin Modération" path="/admin" robots="noindex,nofollow" />
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-black text-slate-900">Admin modération</h1>
          <p className="mt-2 text-sm text-slate-600">
            Accès privé. Entrez le mot de passe d’administration.
          </p>
          <form onSubmit={handleUnlock} className="mt-5 space-y-3">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              placeholder="Mot de passe admin"
              autoComplete="current-password"
            />
            <button
              type="submit"
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              Déverrouiller
            </button>
          </form>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-[1300px] px-4 py-10">
      <Seo title="Admin Modération" path="/admin" robots="noindex,nofollow" />

      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4">
        <h1 className="text-2xl font-black text-slate-900">Index admin - modération</h1>
        <p className="mt-1 text-sm text-slate-600">
          Liste complète des posts (y compris disabled). Utilisez les actions pour préparer un patch rapide.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Total</p>
            <p className="mt-1 text-2xl font-black text-slate-900">{postStats.total}</p>
          </div>
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Published</p>
            <p className="mt-1 text-2xl font-black text-emerald-800">{postStats.published}</p>
          </div>
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-rose-700">Disabled</p>
            <p className="mt-1 text-2xl font-black text-rose-800">{postStats.disabled}</p>
          </div>
          <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-indigo-700">Affichés (filtres)</p>
            <p className="mt-1 text-2xl font-black text-indigo-800">{filteredPosts.length}</p>
          </div>
        </div>
      </div>

      <div className="mb-4 grid gap-3 md:grid-cols-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="all">Tous statuts</option>
          <option value="published">Published</option>
          <option value="disabled">Disabled</option>
        </select>

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="all">Toutes catégories</option>
          {categories.map((category) => (
            <option key={category.slug} value={category.slug}>
              {category.label}
            </option>
          ))}
        </select>

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm md:col-span-2"
          placeholder="Recherche titre..."
        />
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
              <th className="px-3 py-3">Date</th>
              <th className="px-3 py-3">Catégorie</th>
              <th className="px-3 py-3">Photo</th>
              <th className="px-3 py-3">Titre</th>
              <th className="px-3 py-3">Status</th>
              <th className="px-3 py-3">Slug</th>
              <th className="px-3 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPosts.map((post) => {
              const exactPath = String(post.filePath || '').replace(/^\.\//, '');
              const githubUrl = `${REPO_EDIT_BASE}${exactPath}`;
              const disableInstruction = `Ouvre ${exactPath} et remplace status: published par status: disabled`;
              const enableInstruction = `Ouvre ${exactPath} et remplace status: disabled par status: published`;
              const removeCoverInstruction = buildRemoveCoverInstruction(exactPath);
              const removeCommand = `rm \"${exactPath}\" && git commit -m \"chore: remove ${post.slug}\"`;
              const suggestedAction =
                post.status === 'disabled'
                  ? { label: 'Suggestion: ENABLE', className: 'bg-emerald-100 text-emerald-700' }
                  : { label: 'Suggestion: DISABLE', className: 'bg-amber-100 text-amber-700' };
              const coverValue = String(post.cover || '').trim();

              return (
                <tr key={post.slug} className="border-t border-slate-100 align-top">
                  <td className="px-3 py-3 text-slate-600">{post.date}</td>
                  <td className="px-3 py-3 text-slate-700">{post.categoryLabel}</td>
                  <td className="px-3 py-3">
                    {coverValue ? (
                      <div className="space-y-2">
                        <img
                          src={coverValue}
                          alt={`Cover ${post.slug}`}
                          loading="lazy"
                          className="h-16 w-28 rounded-md border border-slate-200 object-cover"
                        />
                        <a
                          href={coverValue}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-block text-[11px] font-semibold text-indigo-700 hover:underline"
                        >
                          Ouvrir l'image
                        </a>
                      </div>
                    ) : (
                      <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500">
                        Aucune image
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 font-medium text-slate-900">{post.title}</td>
                  <td className="px-3 py-3">
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-semibold ${
                        post.status === 'disabled'
                          ? 'bg-rose-100 text-rose-700'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}
                    >
                      {post.status}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-slate-500">{post.slug}</td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-2">
                      <span
                        className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${suggestedAction.className}`}
                      >
                        {suggestedAction.label}
                      </span>
                      <button
                        type="button"
                        onClick={() => copyText(disableInstruction, `d-${post.slug}`)}
                        className="rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Copier patch DISABLE
                      </button>
                      <button
                        type="button"
                        onClick={() => copyText(enableInstruction, `e-${post.slug}`)}
                        className="rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Copier patch ENABLE
                      </button>
                      <button
                        type="button"
                        onClick={() => copyText(removeCoverInstruction, `rmc-${post.slug}`)}
                        className="rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Supprimer image
                      </button>
                      <button
                        type="button"
                        onClick={() => handleReplaceCover(post, exactPath)}
                        className="rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Remplacer image
                      </button>
                      <a
                        href={githubUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Ouvrir sur GitHub
                      </a>
                      <button
                        type="button"
                        onClick={() => copyText(removeCommand, `r-${post.slug}`)}
                        className="rounded-md border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Copier commande suppression
                      </button>
                    </div>
                    {copied === `d-${post.slug}` ||
                    copied === `e-${post.slug}` ||
                    copied === `r-${post.slug}` ||
                    copied === `rmc-${post.slug}` ||
                    copied === `rc-${post.slug}` ? (
                      <p className="mt-1 text-xs text-emerald-600">Copié.</p>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default AdminIndex;
